import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRIMARY = '#2b6cb0';
const PRIMARY_LIGHT = '#ebf8ff';

function adminHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('hrv_admin_token')}` };
}

// ── Palette ────────────────────────────────────────────────────────────────────
const COLORS = {
  blue:   { bg: '#ebf8ff', border: '#bee3f8', text: PRIMARY,   dark: '#1a365d' },
  teal:   { bg: '#e6fffa', border: '#81e6d9', text: '#2c7a7b', dark: '#1d4044' },
  green:  { bg: '#f0fff4', border: '#9ae6b4', text: '#276749', dark: '#1c4532' },
  purple: { bg: '#faf5ff', border: '#d6bcfa', text: '#6b46c1', dark: '#322659' },
  amber:  { bg: '#fffbeb', border: '#fbd38d', text: '#92400e', dark: '#451a03' },
  rose:   { bg: '#fff5f5', border: '#fed7d7', text: '#c53030', dark: '#63171b' },
  indigo: { bg: '#eef2ff', border: '#c7d2fe', text: '#4338ca', dark: '#1e1b4b' },
};

// ── Stat card ──────────────────────────────────────────────────────────────────
function Stat({ label, value, sub, palette, onClick, topRight }) {
  const c = COLORS[palette] || COLORS.blue;
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover && onClick ? c.bg : 'white',
        border: `1px solid ${hover && onClick ? c.border : '#e2e8f0'}`,
        borderRadius: '12px',
        padding: '18px 20px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s',
        boxShadow: hover && onClick ? `0 4px 12px ${c.border}` : '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
      }}
    >
      {topRight && (
        <div style={{ position: 'absolute', top: '12px', right: '14px', fontSize: '0.7rem', color: c.text, fontWeight: '700', background: c.bg, border: `1px solid ${c.border}`, padding: '1px 7px', borderRadius: '10px' }}>
          {topRight}
        </div>
      )}
      <div style={{ fontSize: '1.8rem', fontWeight: '800', color: c.dark, lineHeight: 1 }}>
        {value ?? <span style={{ color: '#cbd5e0' }}>—</span>}
      </div>
      <div style={{ color: '#4a5568', fontSize: '0.82rem', marginTop: '5px', fontWeight: '500' }}>{label}</div>
      {sub && <div style={{ color: '#a0aec0', fontSize: '0.74rem', marginTop: '3px' }}>{sub}</div>}
      {onClick && <div style={{ position: 'absolute', bottom: '10px', right: '14px', color: c.text, fontSize: '0.72rem', opacity: hover ? 1 : 0, transition: 'opacity 0.15s' }}>View →</div>}
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px', marginTop: '4px' }}>
      {children}
    </div>
  );
}

// ── Status pill ────────────────────────────────────────────────────────────────
function StatusPill({ label, ok, loading, onClick }) {
  const color = loading ? '#a0aec0' : ok ? '#38a169' : '#e53e3e';
  const bg    = loading ? '#f7fafc' : ok ? '#f0fff4' : '#fff5f5';
  const border= loading ? '#e2e8f0' : ok ? '#9ae6b4' : '#fed7d7';
  return (
    <div onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', background: bg, border: `1px solid ${border}`, borderRadius: '20px', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color }} />
      <span style={{ fontSize: '0.75rem', fontWeight: '600', color }}>{label}</span>
    </div>
  );
}

// ── Date filter presets ────────────────────────────────────────────────────────
const PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All time', days: 0 },
];

// ── Chart tooltip ──────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.8rem' }}>
      <div style={{ fontWeight: '700', color: '#2d3748', marginBottom: '4px' }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginTop: '2px' }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [metrics,  setMetrics]  = useState(null);
  const [activity, setActivity] = useState(null);
  const [aiStats,  setAiStats]  = useState(null);
  const [sysSettings, setSysSettings] = useState({});
  const [days, setDays]   = useState(30);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback((d) => {
    const h = adminHeaders();
    setMetrics(null);
    fetch(`${API_URL}/admin/metrics?days=${d}`, { headers: h })
      .then(r => r.json()).then(data => { if (data.error) setError(data.error); else setMetrics(data); })
      .catch(() => setError('Failed to load metrics'));

    fetch(`${API_URL}/admin/activity?days=${d || 30}`, { headers: h })
      .then(r => r.json()).then(data => {
        if (!data.error) {
          setActivity(data.dates.map((date, i) => ({
            date: date.slice(5),
            signups: data.signups[i],
            labs: data.lab_uploads[i],
            docs: data.document_uploads[i],
          })));
        }
      }).catch(() => {});

    fetch(`${API_URL}/admin/ai/stats`, { headers: h })
      .then(r => r.json()).then(data => { if (!data.error) setAiStats(data); }).catch(() => {});

    fetch(`${API_URL}/admin/settings`, { headers: h })
      .then(r => r.json()).then(data => { if (!data.error) setSysSettings(data.settings || {}); }).catch(() => {});
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const m = metrics;
  const isMaintenance = sysSettings.maintenance_mode === 'true';
  const ollamaOnline  = aiStats?.ollama_status === 'online';

  const periodLabel = days === 0 ? 'all time' : `last ${days}d`;

  return (
    <AdminLayout>
      {/* ── Header + filter row ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1a202c', margin: 0 }}>Platform Overview</h1>
          <p style={{ color: '#718096', margin: '4px 0 0', fontSize: '0.85rem' }}>
            Showing data for: <strong style={{ color: PRIMARY }}>{periodLabel}</strong>
          </p>
        </div>

        {/* System status — compact inline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <StatusPill label={`Ollama ${ollamaOnline ? 'Online' : 'Offline'}`} ok={ollamaOnline} loading={!aiStats} onClick={() => navigate('/admin/ai')} />
          <StatusPill label={isMaintenance ? 'Maintenance ON' : 'Maintenance Off'} ok={!isMaintenance} onClick={() => navigate('/admin/settings')} />
          {aiStats?.total_calls_today != null && (
            <div style={{ padding: '4px 10px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', color: '#4338ca' }}>
              {aiStats.total_calls_today} AI calls today
            </div>
          )}
          {aiStats?.failed_calls_24h > 0 && (
            <div onClick={() => navigate('/admin/ai')} style={{ padding: '4px 10px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', color: '#c53030', cursor: 'pointer' }}>
              ⚠ {aiStats.failed_calls_24h} failed
            </div>
          )}
        </div>
      </div>

      {/* Date range filter */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', background: 'white', padding: '6px', borderRadius: '10px', border: '1px solid #e2e8f0', alignSelf: 'flex-start', width: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {PRESETS.map(p => (
          <button key={p.label} onClick={() => setDays(p.days)} style={{
            padding: '7px 16px', borderRadius: '7px', border: 'none', fontWeight: '600', fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.12s',
            background: days === p.days ? PRIMARY : 'transparent',
            color: days === p.days ? 'white' : '#718096',
            boxShadow: days === p.days ? `0 2px 6px ${PRIMARY}40` : 'none',
          }}>
            {p.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', color: '#c53030', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* ── Row 1: Platform data ── */}
      <SectionLabel>Platform Data</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <Stat label="Total Patients"       value={m?.patients?.total}           palette="blue"   sub={days ? `+${m?.patients?.in_period ?? '…'} this period` : undefined} onClick={() => navigate('/admin/users')} />
        <Stat label="Unique Lab Tests"     value={m?.lab_results?.unique_test_names} palette="teal"  sub="distinct parameters tracked" />
        <Stat label="Lab Results"          value={days ? m?.lab_results?.in_period : m?.lab_results?.total} palette="purple" sub={days ? `of ${m?.lab_results?.total ?? '…'} total` : 'all time'} />
        <Stat label="Vitals Added"         value={days ? m?.vitals?.in_period : m?.vitals?.total} palette="green"  sub={days ? `of ${m?.vitals?.total ?? '…'} total` : 'all time'} />
        <Stat label="Docs Uploaded"        value={days ? m?.documents?.in_period : m?.documents?.total} palette="amber"  sub={`${m?.documents?.total_size_mb ?? '…'} MB total storage`} />
        <Stat label="Active Medications"   value={m?.medications?.active}       palette="rose"   sub={`of ${m?.medications?.total ?? '…'} prescribed`} />
      </div>

      {/* ── Row 2: User stats ── */}
      <SectionLabel>User Activity</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <Stat label="Total Users"       value={m?.users?.total}          palette="blue"  onClick={() => navigate('/admin/users')} topRight="click to view" />
        <Stat label={days ? `New (${periodLabel})` : 'All Signups'}
                                        value={days ? m?.users?.new_in_period : m?.users?.total} palette="teal" sub={`${m?.users?.new_this_week ?? '…'} this week`} />
        <Stat label="Active (7 days)"   value={m?.users?.active_7d}      palette="green"  sub="logged in last 7 days" />
        <Stat label="Active (30 days)"  value={m?.users?.active_30d}     palette="indigo" sub="logged in last 30 days" />
        <Stat label="New This Month"    value={m?.users?.new_this_month}  palette="purple" />
        <Stat label="Avg Labs / Patient" value={m?.patients?.total ? Math.round((m?.lab_results?.total ?? 0) / m.patients.total) : '—'} palette="amber" sub="lab results per patient" />
      </div>

      {/* ── Row 3: Highlights ── */}
      <SectionLabel>Content Highlights</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {m?.vitals?.top_type && (
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Top Vital Type</div>
            <div style={{ fontWeight: '700', color: '#2d3748', fontSize: '1rem' }}>{m.vitals.top_type.name?.replace(/_/g, ' ')}</div>
            <div style={{ color: '#718096', fontSize: '0.8rem', marginTop: '2px' }}>{m.vitals.top_type.count} readings recorded</div>
          </div>
        )}
        {m?.lab_results?.top_category && (
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Top Lab Category</div>
            <div style={{ fontWeight: '700', color: '#2d3748', fontSize: '1rem' }}>{m.lab_results.top_category.name || 'General'}</div>
            <div style={{ color: '#718096', fontSize: '0.8rem', marginTop: '2px' }}>{m.lab_results.top_category.count} results</div>
          </div>
        )}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Storage Used</div>
          <div style={{ fontWeight: '700', color: '#2d3748', fontSize: '1rem' }}>{m?.documents?.total_size_mb ?? '—'} MB</div>
          <div style={{ color: '#718096', fontSize: '0.8rem', marginTop: '2px' }}>{m?.documents?.total ?? '—'} documents total</div>
        </div>
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>AI Monitoring</div>
          <div style={{ fontWeight: '700', color: '#2d3748', fontSize: '1rem' }}>{aiStats?.success_rate != null ? `${aiStats.success_rate}%` : '—'} success rate</div>
          <div style={{ color: '#718096', fontSize: '0.8rem', marginTop: '2px', cursor: 'pointer', color: PRIMARY }} onClick={() => navigate('/admin/ai')}>
            {aiStats?.total_calls_today ?? '—'} calls today · View logs →
          </div>
        </div>
      </div>

      {/* ── Charts ── */}
      {activity && (
        <>
          <SectionLabel>Trends</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '8px' }}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: '700', color: '#2d3748', marginBottom: '16px', fontSize: '0.9rem' }}>User Signups</div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a0aec0' }} tickLine={false} axisLine={false}
                    interval={Math.max(0, Math.floor(activity.length / 6) - 1)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#a0aec0' }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="signups" stroke={PRIMARY} strokeWidth={2.5} dot={false} name="Signups" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: '700', color: '#2d3748', marginBottom: '16px', fontSize: '0.9rem' }}>Daily Uploads</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a0aec0' }} tickLine={false} axisLine={false}
                    interval={Math.max(0, Math.floor(activity.length / 6) - 1)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#a0aec0' }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar dataKey="labs" fill="#6b46c1" name="Lab Uploads" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="docs" fill="#92400e" name="Documents" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
